import React, { useContext, useEffect, useRef, useState } from 'react';

import axios from 'axios';

import { Answer, Question } from '~/types';
import UploadPhoto from './UploadPhoto';
import StyledTextArea from '~/components/StyledTextArea';
import Fade from '~/components/Fade';
import { useTranslation } from 'react-i18next';
import SendButton from '~/components/SendButton';
import { AppContext } from '~/contexts/AppContext';
import { MenuScreen } from '~/App';
import { useParams } from 'react-router-dom';
import MatomoTracker from '~/util/matomo/MatomoTracker';
import * as topicUtils from '~/util/topic';
import { QuestionContext } from '~/contexts/QuestionContext';

interface LinkButtonProps {
    onClick: () => void;
    children: React.ReactNode;
}

const LinkButton: React.FC<LinkButtonProps> = ({ onClick, children }) => {

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        onClick();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        if (e.key === 'Enter' || e.key === ' ') {
            onClick();
        }
    };

    return (
        <a
            href='#'
            role="button"
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            onAuxClick={(e) => e.preventDefault()}
            style={{ color: 'white', fontWeight: 'bold' }}
        >
            {children}
        </a>
    );
};

export type SetImageFunction = (imageUrl: string) => void;
export type DeleteImageFunction = () => void;

const OpenEndedAnswer: React.FC = () => {

    const [state, setState] = useState<{ text: string; imageUrl: string | null }>({ text: '', imageUrl: null });

    const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

    const { topicData, setMenuScreen, apiUrl, preciseCoordinates } = useContext(AppContext)!;
    const { ratingId, question, questionTime, onSubmitAnswer } = useContext(QuestionContext)!;

    const { t, i18n } = useTranslation();
    const { topicHash }: { topicHash: string } = useParams();

    const submitAnswer = async () => {

        await axios.post(`${apiUrl}/rating-answer/${topicData?.topic.id}/open-ended`, {
            ratingId: ratingId,
            questionnaireId: question.questionnaireId,
            questionId: question.questionId,
            preciseCoordinates: preciseCoordinates,
            timeToAnswerMs: !questionTime ? 0 : Date.now() - questionTime,
            lang: i18n.language,
            ...(state.text ? { answerText: state.text } : {}),
            ...(state.imageUrl ? { answerUrl: state.imageUrl } : {}),
        });

        MatomoTracker.trackEvent({
            category: 'qna', 
            action: 'submit-answer',
            documentTitle: topicData?.topic.name,
            href: `https://kazva.bg/${topicHash}`,
            customDimensions: [
                { id: 3, value: question.questionText },
                { id: 4, value: state.text },
                { id: 5, value: '' }
            ]
        });

        onSubmitAnswer(null);
    };

    const handleChange = (text: string) => {
        setState({ ...state, text: text ?? '' });

        if (textAreaRef.current) {
            textAreaRef.current.style.height = '0';
            textAreaRef.current.style.height = textAreaRef.current.scrollHeight + 'px';
            textAreaRef.current.style.overflowY = 'hidden';
        }
    };

    useEffect(() => {
        if (textAreaRef.current) {
            textAreaRef.current.style.height = '0';
            textAreaRef.current.style.height = textAreaRef.current.scrollHeight + 'px';
            textAreaRef.current.style.overflowY = 'hidden';
        }
    }, [textAreaRef.current]);

    const setImage: SetImageFunction = (imageUrl: string) => {
        setState({ ...state, imageUrl });
    };

    const deleteImage = () => {
        setState({ ...state, imageUrl: null });
    };

    const clickPolicy = () => {

        MatomoTracker.trackEvent({
            category: 'qna',
            action: 'open-privacy-policy',
            documentTitle: topicData?.topic.name,
            href: `https://kazva.bg/${topicHash}`
        });

        setMenuScreen(MenuScreen.PrivacyNoticeQna);
    };

    const answer = question.answers[0];

    return (
        <Fade cascade>
            <label>
                <StyledTextArea
                    ref={textAreaRef}
                    onChange={e => handleChange(e.target.value)}
                    placeholder={topicUtils.getAnswerText(
                        topicData,
                        question.questionId,
                        answer.answerId,
                        i18n.language
                    )}
                />
                {!answer.canAttachImage ? null : <UploadPhoto setImage={setImage} deleteImage={deleteImage} value={state} />}
                <SendButton onClick={submitAnswer}/>
                <p style={{
                    padding: '0 10px',
                    fontSize: '12px',
                    color: '#D9D9D9',
                    marginBottom: '20px'
                }}>
                    {t('qna_open_ended_disclaimer')} <LinkButton onClick={clickPolicy}>{t('qna_open_ended_disclaimer_link')}</LinkButton>.
                </p>
            </label>
        </Fade>
    );
};

export default OpenEndedAnswer;